import { __rest } from '../../_virtual/_tslib.js';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/quantity.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import { InnerRefPropType } from '../../packages/props-utils/prop-types.js';
import { decrementValue, constraintValue, parseValue, incrementValue } from './quantity-utils.js';
import { InputInfoChildren, InputInfo } from '../_atoms/input-info/input-info.js';
import { inputLabelValidator } from '../../prop-types-validators/input-label-validator.js';
import Button from '../button/button.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const bem = new HoneyBem('quantity', styles);
function Quantity(_a) {
  var {
      active = false,
      caption,
      disabled = false,
      error = false,
      extraClasses,
      id,
      info,
      infoError,
      inline = false,
      innerRef,
      label,
      max = 100,
      min = 0,
      minusBtnProps = {},
      plusBtnProps = {},
      step = 1,
      value = 0
    } = _a,
    rest = __rest(_a, ["active", "caption", "disabled", "error", "extraClasses", "id", "info", "infoError", "inline", "innerRef", "label", "max", "min", "minusBtnProps", "plusBtnProps", "step", "value"]);
  const [val, setVal] = useState(value);
  const numberInput = useRef(null);
  // catching custom button onClick handlers
  // executing them alongside increment/decrement functionality
  const {
      onClick: minusBtnOnClick
    } = minusBtnProps,
    minusBtnRest = __rest(minusBtnProps, ["onClick"]);
  const {
      onClick: plusBtnOnClick
    } = plusBtnProps,
    plusBtnRest = __rest(plusBtnProps, ["onClick"]);
  // when user changes the value prop align the internal value
  // useState will be fired only on first render
  useEffect(() => {
    setVal(value);
  }, [value]);
  /**
   * Converts input value to integer if necessary and calls
   * setVal with the resulting value
   * @param newValue - number or string
   */
  const setValue = newValue => setVal(parseValue(newValue));
  /**
   * handles arrow up and arrow down keypress
   * - sets value with utils incremented or decremented value accordingly
   * - calls onKeyDown callback with input event object
   * @param e input event object
   */
  const handleKeyDown = e => {
    var _a;
    if (e.key === 'ArrowDown' || e.keyCode === 40) {
      // disabling native number input keyboard event handling to prevent value double increments
      e.preventDefault();
      setValue(decrementValue(val, step, min));
    }
    if (e.key === 'ArrowUp' || e.keyCode === 38 || e.key === ' ' || e.keyCode === 32) {
      // disabling native number input keyboard event handling to prevent value double increments
      e.preventDefault();
      setValue(incrementValue(val, step, max));
    }
    (_a = rest.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(rest, e);
  };
  /**
   * handles input change
   * - sets state value with numeric version of user input value
   * - calls onChange callback with input event object
   * @param e - input event object
   */
  const handleInputChange = e => {
    var _a;
    setValue(e.target.value);
    (_a = rest.onChange) === null || _a === void 0 ? void 0 : _a.call(rest, e);
  };
  /**
   * handles input blur
   * - sets state with validated input value (numeric within min/max constraints)
   * - calls onBlur callback with input event object
   * @param e - input event object
   */
  const handleInputBlur = e => {
    var _a;
    setValue(constraintValue(e.target.value, min, max));
    (_a = rest.onBlur) === null || _a === void 0 ? void 0 : _a.call(rest, e);
  };
  /**
   * handles minus button click
   * @param e - input event object
   */
  const handleMinusClick = e => {
    setValue(decrementValue(val, step, min));
    if (typeof minusBtnOnClick === 'function') {
      minusBtnOnClick(e);
    }
  };
  /**
   * handles plus button click
   * @param e - input event object
   */
  const handlePlusClick = e => {
    setValue(incrementValue(val, step, max));
    if (typeof plusBtnOnClick === 'function') {
      plusBtnOnClick(e);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: bem.b([{
      inline,
      error,
      active,
      disabled
    }], extraClasses)
  }, label && (/*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    className: bem.e('label')
  }, label, caption && /*#__PURE__*/React.createElement("small", null, caption))), /*#__PURE__*/React.createElement("div", {
    className: bem.e('picker')
  }, /*#__PURE__*/React.createElement(Button, Object.assign({
    extraClasses: bem.e('button', 'minus'),
    disabled: disabled || val <= min,
    display: "square",
    onClick: handleMinusClick,
    "aria-controls": id
  }, minusBtnRest)), /*#__PURE__*/React.createElement("input", Object.assign({
    ref: node => {
      reactUtils.assignInnerRefWithComponentRef(node, innerRef, numberInput);
    },
    type: "number",
    role: "spinbutton",
    id: id,
    className: bem.e('input'),
    disabled: disabled,
    min: min,
    max: max,
    step: step,
    value: val,
    "aria-live": "assertive"
  }, error && {
    'aria-invalid': true
  }, error && infoError && {
    'aria-errormessage': "infoError-".concat(id)
  }, info && {
    'aria-describedby': "info-".concat(id)
  }, rest, {
    onKeyDown: handleKeyDown,
    onBlur: handleInputBlur,
    onChange: handleInputChange
  })), /*#__PURE__*/React.createElement(Button, Object.assign({
    extraClasses: bem.e('button', 'plus'),
    disabled: disabled || val >= max,
    display: "square",
    onClick: handlePlusClick,
    "aria-controls": id
  }, plusBtnRest))), error && infoError && (/*#__PURE__*/React.createElement(InputInfo, {
    error: true,
    id: id,
    className: bem.e('info')
  }, infoError)), info && (/*#__PURE__*/React.createElement(InputInfo, {
    id: id,
    className: bem.e('info')
  }, info)));
}
Quantity.displayName = 'Quantity';
Quantity.propTypes = {
  active: PropTypes.bool,
  caption: PropTypes.string,
  extraClasses: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  info: InputInfoChildren,
  infoError: InputInfoChildren,
  inline: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  innerRef: InnerRefPropType,
  label: inputLabelValidator('Quantity'),
  max: PropTypes.number,
  min: PropTypes.number,
  minusBtnProps: PropTypes.shape({}),
  plusBtnProps: PropTypes.shape({}),
  step: PropTypes.number,
  value: PropTypes.number
};
var quantity = Injecss(Quantity, styles);

export { Quantity, quantity as default };
